import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentType, SVGProps } from 'react';
import { BookOpen as LucideBookOpen } from 'lucide-react';
import { usePlayerRuntime } from './hooks/usePlayerRuntime';
import { DevQaPanel } from './components/dev/DevQaPanel';
import { StepLayoutRenderer } from './components/layouts/StepLayoutRenderer';
import { MinigameHost } from './components/minigame/MinigameHost';
import { StepInteractionOverlay } from './components/overlays/StepInteractionOverlay';
import { BgmController } from './components/audio/BgmController';
import { GlobalAudioControl } from './components/audio/GlobalAudioControl';
import { StartScreen } from './components/StartScreen';
import { useAudio } from './audio/useAudio';
import { audioEngine } from './audio/audioEngine';
import { SupernaturalOverlay, useSupernatural, getTimeOfDay } from './atmosphere';
import { ClueWindow } from './components/ClueWindow';
import { StatusHUD } from './components/StatusHUD';
import { RouteMapButton, RouteMapWindow } from './components/RouteMapWindow';
import { LineHistoryButton } from './components/line-panel/LineHistoryButton';
import { LineHistoryPanel } from './components/line-panel/LineHistoryPanel';
import { saveProgress, loadProgress, clearProgress, getProgressSummary, isProgressCompatible } from './utils/saveProgress';
import { mintPlaythroughId } from './utils/playthrough';
import type { ClueWindowState, OverlayElement, Mood } from 'shared';
import { unlockClue, getUnreadClueCount, MoodSchema } from 'shared';
import { resolveMediaRefToUrl } from './utils/mediaRef';
import { buildRouteMapForCurrentStep } from './utils/routeMapAdapter';
import { getCurrentDialogIndex } from './engine/cursorManagement';
// 本番モード判定（?prod=1 / ?productionMode=1 / VITE_PRODUCTION_MODE）は
// utils/productionMode.ts に一元化（最終監査 §2-1-4: 判定の重複を残さない）。
import { useProductionMode } from './utils/productionMode';

// Phase E3 (Mood Switcher): URL の ?mood=<mood> で scenario の mood を上書きする開発用機能。
// 同じシナリオを別 mood で見比べ・パレット調整するため。語彙の二重管理を避けるため、
// 検証は shared の MoodSchema に委ねる。無指定 or 無効値（例 'spooky'）は null = フォールバック。
function useMoodOverride(): Mood | null {
  return useMemo(() => {
    if (typeof window !== 'undefined') {
      const raw = new URLSearchParams(window.location.search).get('mood');
      const parsed = MoodSchema.safeParse(raw);
      if (parsed.success) return parsed.data;
    }
    return null;
  }, []);
}

// プレイヤーフェーズ: スタート画面 -> プレイ中
type PlayerPhase = 'start_screen' | 'playing';

// P7-5: HUD の絵文字廃止。シェルは lucide-react を直接 import する流儀（RouteMapButton踏襲）。
type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;
const BookOpen = LucideBookOpen as unknown as IconComponent;

export function App() {
  const { status, error, snapshot, waiting, actions, isBusy, mode, context, lastSavedAt, preload, startScreen, scenarioId, scenario, restoreState, variableDefinitions, clueDefinitions, scenarioUi } = usePlayerRuntime();
  const [showLoadingHint, setShowLoadingHint] = useState(false);
  const [ui05Open, setUi05Open] = useState(false);
  const [sceneVideoEnded, setSceneVideoEnded] = useState(false);
  const [playerPhase, setPlayerPhase] = useState<PlayerPhase>('start_screen');
  const [clueWindowOpen, setClueWindowOpen] = useState(false);
  const [routeMapOpen, setRouteMapOpen] = useState(false);
  const [lineHistoryOpen, setLineHistoryOpen] = useState(false);
  const [clueState, setClueState] = useState<ClueWindowState>({ clues: {} });
  // P9-1: プレイスルー毎に一意な id（図鑑記録の冪等キー）。mock 経路の context.runId は
  // 固定文字列（'mock-run' 等）でラン毎に一意でないため使えない。新規開始（最初から）で
  // 再採番し、「続きから」は進行保存に載せた id を復元して同一プレイスルーを維持する。
  const [playthroughId, setPlaythroughId] = useState<string>(() => mintPlaythroughId());
  const ui05SupportUrl = useMemo(() => import.meta.env.VITE_SUPPORT_URL as string | undefined, []);
  const productionMode = useProductionMode();
  const moodOverride = useMoodOverride();
  const { setAmbient } = useAudio();
  // P2「端末は付喪神」Wave B: 気配（超常）演出の制御ハンドル。
  // playConnectionSequence は起動「……接続」演出、setAmbientDistortion は mood/深夜連動の極薄ノイズに使う。
  // P8-1: pulse は「mood が dread へ切り替わった瞬間」の単発の気配に 1 箇所だけ配線する。
  const { setAmbientDistortion, playConnectionSequence, pulse } = useSupernatural();

  // 現在適用すべき mood（URL ?mood > 現在 node の mood > scenario.theme.mood.primary）。
  // data-mood / 合成アンビエント / 気配ノイズ / dread 切替 pulse が同一ソースに追従するよう、一箇所で導出する。
  // P8-4: node.mood（optional）により node 遷移で mood がランタイム切替される（mood の両肺化）。
  // mood 未指定 node は従来どおり theme.mood.primary へフォールバック＝既存シナリオは挙動不変。
  const effectiveMood = moodOverride ?? snapshot?.node?.mood ?? scenario?.theme?.mood?.primary ?? null;

  // Phase D: scenario の mood を <html data-mood> に反映。Foundation の CSS variables が追従する。
  // Phase E3: URL の ?mood が指定されていれば scenario theme より優先する（開発用の見比べ）。
  // 優先順位: URL(?mood) > node.mood(P8-4) > scenario.theme.mood.primary。いずれも無ければ属性を外し従来の見た目に戻す（後方互換）。
  useEffect(() => {
    if (effectiveMood) document.documentElement.setAttribute('data-mood', effectiveMood);
    else document.documentElement.removeAttribute('data-mood');
  }, [effectiveMood]);

  // R2 感覚層（Wave2A）: プレイ中は scenario の mood に合わせて合成アンビエント（ドローン）を鳴らす。
  // data-mood と同じ effectiveMood に追従。プレイ中以外（スタート画面/ローディング/離脱）は null で停止。
  // mood 未指定 or アンビ無音 mood（playful/comedic）では engine 側が無音に倒すため回帰なし。
  useEffect(() => {
    if (playerPhase !== 'playing') {
      setAmbient(null);
      return;
    }
    setAmbient(effectiveMood);
    return () => setAmbient(null);
  }, [playerPhase, effectiveMood, setAmbient]);

  // P2「端末は付喪神」Wave B: mood 連動の「気配（超常）」を極薄で重ねる。
  // - dread のとき setAmbientDistortion(0.25,'noise')＝うっすらノイズが画面に滲む（やりすぎない）。
  // - mystic は更に薄く 0.15（不穏というより「何かが近い」程度）。
  // - 深夜帯（getTimeOfDay().isDeepNight）は一段強める（+0.12・上限 0.4）＝丑三つ時に気配が濃くなる。
  // - それ以外の mood / プレイ中以外は 0 で停止（回帰非破壊・既定は気配なし）。
  // 世界観 §7.4: これは “現象” であって人格ではない。色を塗り替えず暗いノイズの滲みのみ（atmosphere.css 側で担保）。
  useEffect(() => {
    if (playerPhase !== 'playing') {
      setAmbientDistortion(0);
      return;
    }
    const baseByMood: Record<string, number> = { dread: 0.25, mystic: 0.15 };
    const base = baseByMood[effectiveMood ?? ''] ?? 0;
    if (base <= 0) {
      setAmbientDistortion(0);
      return;
    }
    const deepNight = getTimeOfDay().isDeepNight;
    const intensity = Math.min(0.4, deepNight ? base + 0.12 : base);
    setAmbientDistortion(intensity, 'noise');
    return () => setAmbientDistortion(0);
  }, [playerPhase, effectiveMood, setAmbientDistortion]);

  // P8-1: 死蔵 API pulse() の唯一の配線。プレイ中に mood が dread へ「切り替わった瞬間」だけ、
  // 単発の気配（短いグリッチ）を走らせる。開始時から dread のシナリオでは発火しない（切替のみ）。
  // P8-4 更新: node.mood によるシナリオ途中の mood 切替機構が入ったため、node 遷移で
  // dread へ切り替わる本番シナリオではこの pulse が実際に発火する（P8-1 時点の「機構待ち」注記を解消）。
  // connection と pulse は Provider 内で同一 transient スロットを共有するが、dread 切替は
  // playing 中（接続演出の後）に限るため衝突しない。
  const prevMoodRef = useRef<Mood | null>(null);
  useEffect(() => {
    const prev = prevMoodRef.current;
    prevMoodRef.current = playerPhase === 'playing' ? effectiveMood : null;
    if (playerPhase !== 'playing') return;
    if (effectiveMood === 'dread' && prev !== null && prev !== 'dread') {
      pulse({ intensity: 0.35, durationMs: 600 });
    }
  }, [playerPhase, effectiveMood, pulse]);

  // 手がかりの自動解放（変数と連動）
  useEffect(() => {
    if (!clueDefinitions || clueDefinitions.length === 0 || !snapshot?.state?.variables) return;

    let newState = clueState;
    for (const clue of clueDefinitions) {
      const variableValue = snapshot.state.variables[clue.unlockVariable];
      // 変数が「有効」とみなせるかチェック:
      // - boolean: true のみ
      // - number: 0より大きい値
      // - string: 空でない文字列、または "true"
      const isUnlocked =
        variableValue === true ||
        (typeof variableValue === 'number' && variableValue > 0) ||
        (typeof variableValue === 'string' && (variableValue === 'true' || variableValue.length > 0));

      if (isUnlocked && !newState.clues[clue.id]?.unlocked) {
        newState = unlockClue(newState, clue.id);
      }
    }
    if (newState !== clueState) {
      setClueState(newState);
    }
  }, [clueDefinitions, snapshot?.state?.variables, clueState]);

  // 未読手がかり数
  const unreadClueCount = useMemo(() => getUnreadClueCount(clueState), [clueState]);
  const hasClues = clueDefinitions && clueDefinitions.length > 0;

  // 最後のscene背景を追跡（ミニゲームへ引き継ぐ用）
  const [lastSceneBackground, setLastSceneBackground] = useState<string | null>(null);

  // scene stepの背景を追跡
  useEffect(() => {
    const step = snapshot?.step;
    if (step?.type === 'scene' && step.bg?.kind === 'image' && step.bg?.src) {
      setLastSceneBackground(step.bg.src);
    }
  }, [snapshot?.step]);

  // 現在の背景状態（ミニゲームへ引き継ぐ用）
  const currentBackground = useMemo(() => {
    if (!lastSceneBackground) return null;
    return {
      type: 'image' as const,
      value: lastSceneBackground,
      overlay: 'rgba(0,0,0,0.3)'
    };
  }, [lastSceneBackground]);

  const shouldResetProgressFromUrl = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('reset') === '1' || params.get('fresh') === '1';
  }, []);

  // 保存された進行状況をチェック
  const savedProgressSummary = useMemo(() => {
    if (shouldResetProgressFromUrl) return null;
    if (!scenarioId) return null;
    return getProgressSummary(scenarioId);
  }, [scenarioId, shouldResetProgressFromUrl]);

  const hasSavedProgress = savedProgressSummary !== null;

  // スタート画面設定がない場合は直接プレイ開始
  const hasStartScreen = Boolean(startScreen?.title);
  const shouldShowStartScreen = hasStartScreen && playerPhase === 'start_screen';
  const routeMap = useMemo(() => buildRouteMapForCurrentStep(scenario, snapshot), [scenario, snapshot]);

  useEffect(() => {
    if (!routeMap) {
      setRouteMapOpen(false);
    }
  }, [routeMap]);

  useEffect(() => {
    if (!scenarioId || !shouldResetProgressFromUrl) return;
    clearProgress(scenarioId);
  }, [scenarioId, shouldResetProgressFromUrl]);

  // 進行状況を自動保存（プレイ中のみ）
  useEffect(() => {
    if (playerPhase !== 'playing' || !scenarioId || !snapshot?.state) return;
    // 完了済みの場合は保存しない
    if (snapshot.isCompleted) return;
    // P9-1: playthroughId を進行保存へ載せる（「続きから」で同一プレイスルーを復元するため）。
    saveProgress(scenarioId, snapshot.state, context?.scenarioTitle, playthroughId);
  }, [playerPhase, scenarioId, snapshot?.state, snapshot?.isCompleted, context?.scenarioTitle, playthroughId]);

  // P2 Wave B: 起動「……接続」演出の二重起動ガード。連打で接続シーケンスが多重発火しないようにする。
  // 進行中フラグ（ref）と、duration 後に phase を切替える pending タイマー（unmount/再開で掃除）。
  const startConnectingRef = useRef(false);
  const startTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (startTimerRef.current !== null) clearTimeout(startTimerRef.current);
  }, []);

  // 最初から開始。
  // 世界観 §7.4: 開始＝「端末が異界に勝手に接続される現象」。playConnectionSequence で全画面に
  // 文字化けする "……接続"（状態表示の断片・台詞ではない）を一定時間見せ、その duration の間だけ
  // 接続演出を見せてから本編へ遷移する。深夜帯は intensity を一段上げて気配を濃くする。
  const handleStartGame = useCallback(() => {
    // 二重起動ガード（連打）。進行中なら無視する。
    if (startConnectingRef.current) return;
    startConnectingRef.current = true;

    if (scenarioId) {
      clearProgress(scenarioId);
    }
    // P9-1: 新規プレイスルーの開始＝冪等キーを再採番する（周回で図鑑の clearCount が加算されるように）。
    setPlaythroughId(mintPlaythroughId());

    // 深夜は接続を強める（+0.1・上限 0.7）。reduced-motion は Provider 側で最終 intensity を弱める。
    const deepNight = getTimeOfDay().isDeepNight;
    const reduceMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const intensity = Math.min(0.7, (deepNight ? 0.7 : 0.6));
    // reduced-motion 配慮で演出は短め（通常 1500ms / reduce 600ms）。
    const durationMs = reduceMotion ? 600 : 1500;

    // 接続シーケンス（全画面 distortion + 文字化けする "……接続" ラベル）を発火。
    // 注: ここで supernatural の pulse を重ねてはいけない。pulse と connection は Provider 内で
    // 同一の transient スロット（単一タイマー）を共有するため、pulse が connection を上書きして
    // ラベル消失＋duration 短縮を招く。
    const realDuration = playConnectionSequence({ intensity, durationMs });

    // P8-1: 接続演出の音（従来「音は別扱い」のまま無音だった箇所の解消）。
    // handleStartGame はユーザークリック起点＝unlock 可能。unlock は冪等（AudioProvider の
    // 初回ジェスチャと重複しても安全）。実 duration に同期した低いドローンの立ち上がり＋
    // か細い応答を master gain 経由で鳴らす（mute 中は engine が無視）。
    audioEngine.unlock();
    audioEngine.playConnectionCue(realDuration);

    // 返り値 duration の間だけ接続演出を見せてから本編へ。phase 切替と演出の収束は自然に重なる。
    startTimerRef.current = setTimeout(() => {
      setPlayerPhase('playing');
      startConnectingRef.current = false;
      startTimerRef.current = null;
    }, realDuration);
  }, [scenarioId, playConnectionSequence]);

  // 続きから開始
  const handleResumeGame = useCallback(() => {
    if (!scenarioId) return;
    const saved = loadProgress(scenarioId);
    if (saved && restoreState) {
      // 2026-07-20 傷⑤: 復元前に「保存位置が現行シナリオ構造に実在するか」を検証する。
      // 非互換（シナリオ改稿で node 消失 / step 範囲外）のセーブを復元すると
      // 「……準備中」固定の復元詰みになるため、その場合はセーブを破棄して
      // 新規開始する（ユーザーに見えるのは「最初から始まる」だけ・エラー表示なし）。
      if (!isProgressCompatible(saved.state, scenario)) {
        clearProgress(scenarioId);
        setPlayerPhase('playing');
        return;
      }
      restoreState(saved.state);
      // P9-1: 同一プレイスルーの継続として、保存済み id を復元する（ending 開き直しの二重カウント防止）。
      // 旧セーブ（id 無し）はマウント時採番の id をそのまま使う（後方互換・以後の自動保存で永続化される）。
      if (saved.playthroughId) {
        setPlaythroughId(saved.playthroughId);
      }
    }
    setPlayerPhase('playing');
  }, [scenarioId, restoreState, scenario]);

  // エンディングアクション
  const handleEndingAction = useCallback((action: 'replay' | 'home' | 'url', url?: string) => {
    switch (action) {
      case 'replay':
        // セーブデータをクリアしてリロード（最初からやり直し）
        if (scenarioId) clearProgress(scenarioId);
        window.location.reload();
        break;
      case 'home':
        // スタート画面へ戻る
        if (scenarioId) clearProgress(scenarioId);
        setPlayerPhase('start_screen');
        break;
      case 'url':
        if (url) window.open(url, '_blank');
        break;
    }
  }, [scenarioId]);

  useEffect(() => {
    if (status !== 'loading') {
      setShowLoadingHint(false);
      return;
    }
    const t = setTimeout(() => setShowLoadingHint(true), 6000);
    return () => clearTimeout(t);
  }, [status]);

  // P7-1 自動送り: dialog step で作者が autoAdvance=true を指定した場合、行表示から
  // autoAdvanceDelayMs 経過で自動的に advance() する。エンジン（stepEngine.ts）は不変・
  // タイマーはこの App 層のみで完結させる。
  //
  // 張り直し単位: dialog は 1 step 内に複数 lines を持ち、advance のたびに
  // __dialogCursor（flags）の index が進む。「どの行を今表示しているか」が変わるたびに
  // 前のタイマーを破棄して新しく張り直す必要があるため、armKey に step.id と現在の
  // lineIndex（getCurrentDialogIndex）の両方を含める。
  const autoAdvanceStep = snapshot?.step;
  const autoAdvanceLineIndex =
    autoAdvanceStep?.type === 'dialog' && snapshot?.state
      ? getCurrentDialogIndex(snapshot.state.flags, autoAdvanceStep.id)
      : 0;
  // P7-5: 文字送り（typewriter reveal）が全文表示を終えたかどうか。StepLayoutRenderer →
  // DialogView から onDialogRevealDoneChange 経由で通知される。文字送り中はタイマーを
  // アームしない（起算は全文表示が完了してから）。dialog 以外の step では意味を持たないが
  // 害もない（autoAdvanceCanRun 側で type==='dialog' を別途見ているため）。
  const [dialogRevealDone, setDialogRevealDone] = useState(true);
  const autoAdvanceStepIdForReveal = autoAdvanceStep?.type === 'dialog' ? autoAdvanceStep.id : null;
  useEffect(() => {
    // 行（またはstep）が変わるたびに「まだ文字送り中」からスタートし直す。DialogView 側の
    // useTypewriterReveal も同じ lineKey で 0 文字目から再アームするため、ここで true のまま
    // 放置すると「前の行が全文表示済み」の名残りで一瞬 autoAdvance が誤ってアームされ得る。
    setDialogRevealDone(false);
  }, [autoAdvanceStepIdForReveal, autoAdvanceLineIndex]);
  const autoAdvanceCanRun =
    playerPhase === 'playing' &&
    autoAdvanceStep?.type === 'dialog' &&
    autoAdvanceStep.autoAdvance === true &&
    !snapshot?.isCompleted &&
    !waiting &&
    dialogRevealDone;
  const autoAdvanceDelayMs =
    autoAdvanceStep?.type === 'dialog' ? autoAdvanceStep.autoAdvanceDelayMs ?? 2000 : 2000;
  // 「今アームされているタイマーの識別子」。他コンポーネント（ヘアラインの再生アニメ）に
  // key として渡し、行が変わるたびに CSS アニメーションをリセットさせる。
  const autoAdvanceArmKey = autoAdvanceCanRun ? `${autoAdvanceStep!.id}:${autoAdvanceLineIndex}` : null;

  useEffect(() => {
    if (!autoAdvanceCanRun) return;
    const timer = setTimeout(() => {
      void actions.advance();
    }, autoAdvanceDelayMs);
    // unmount / step変化 / lineIndex変化 / waiting発生のいずれでも張り直し前に必ずクリアする
    // （手動送り（タップ/Enter）は StepLayoutRenderer 側の advance() が直接呼ばれるため、
    // advance 後は step/lineIndex が変わり、このクリーンアップで旧タイマーが破棄される＝二重進行なし）。
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAdvanceArmKey, autoAdvanceCanRun, autoAdvanceDelayMs]);

  if (status === 'error') {
    // P8-0: 本番（prod=1）では ticket/deviceKey/apiBase・409 等の内部情報を出さず、
    // 平易な案内と再読み込み導線のみにする（機械語漏出の封鎖）。dev は従来表示を維持。
    if (productionMode) {
      return (
        <div className="player-shell">
          <div className="player-shell__body">
            <ErrorUi05
              eyebrow={null}
              title="読み込みに失敗しました"
              message="通信環境をご確認のうえ、もう一度お試しください。"
              supportUrl={ui05SupportUrl}
              onRetry={() => window.location.reload()}
            />
          </div>
        </div>
      );
    }
    const debug = getStartupDebugInfo();
    return (
      <div className="player-shell">
        <div className="player-shell__body">
          <ErrorUi05
            title="エラーが発生しました"
            message={`Playerの初期化に失敗しました: ${error}`}
            supportUrl={ui05SupportUrl}
            debugLines={
              debug
                ? [
                    `ticket: ${debug.ticketMasked}`,
                    `deviceKey: ${debug.deviceKey}`,
                    `apiBase: ${debug.apiBase || '(empty)'}`
                  ]
                : []
            }
            onRetry={() => window.location.reload()}
          />
          {debug && (
            <div className="alert-banner" role="note" style={{ marginTop: 12 }}>
              <p className="eyebrow">ヒント（切り分け）</p>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                <li>
                  <strong>A2（端末紐づけ）</strong>: 同じticketを別ブラウザ/別端末で開くと 409（device mismatch）になり得ます。
                </li>
                <li>
                  <strong>期限切れ/クリア済み</strong>: 410（expired/completed）になり得ます。
                </li>
                <li>
                  <strong>VITE_API_URL取り違え</strong>: APIがPages自身に向くとHTMLが返り、初期化に失敗します。
                </li>
              </ul>
              <div style={{ marginTop: 10 }}>
                <p className="eyebrow subtle">debug</p>
                <code>ticket: {debug.ticketMasked}</code>
                <code>deviceKey: {debug.deviceKey}</code>
                <code>apiBase: {debug.apiBase || '(empty)'}</code>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (status === 'loading' || !snapshot) {
    const debug = showLoadingHint ? getStartupDebugInfo() : null;
    const preloadTotal = preload?.total ?? 0;
    const preloadLoaded = preload?.loaded ?? 0;
    const preloadPct = preloadTotal > 0 ? Math.round((preloadLoaded / preloadTotal) * 100) : 0;
    // P8-0: 本番（prod=1）では「UI01 · Loading」eyebrow・asset 数・生URL・失敗件数を出さず、
    // 世界観を壊さない簡素な文言＋進捗バーのみにする。dev は従来表示を維持。
    if (productionMode) {
      return (
        <div className="player-shell">
          <div className="player-shell__body">
            <div className="preload-card">
              <h2>読み込んでいます……</h2>
              {preloadTotal > 0 && (
                <div className="preload-progress" aria-label="読み込み中">
                  <span style={{ width: `${preloadPct}%` }} />
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="player-shell">
        <div className="player-shell__body">
          <div className="preload-card">
            <p className="eyebrow">UI01 · Loading</p>
            <h2>シナリオを準備中</h2>
            {preloadTotal > 0 ? (
              <>
                <div className="preload-progress" aria-label={`preload ${preloadPct}%`}>
                  <span style={{ width: `${preloadPct}%` }} />
                </div>
                <p>{preloadLoaded} / {preloadTotal} assets · {preloadPct}%</p>
                {preload?.currentUrl && <code>{preload.currentUrl}</code>}
                {(preload?.failed.length ?? 0) > 0 && (
                  <p className="error-text">{preload?.failed.length}件の読み込みに失敗しました。開始後の詳細表示で確認できます。</p>
                )}
              </>
            ) : (
              <p>Loading scenario...</p>
            )}
          </div>
          {debug && (
            <div className="alert-banner" role="note" style={{ marginTop: 12 }}>
              <p className="eyebrow">ヒント（切り分け）</p>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                <li>
                  <strong>A2（端末紐づけ）</strong>: 同じticketを別ブラウザ/別端末で開くと 409（device mismatch）になり得ます。
                </li>
                <li>
                  <strong>Network</strong>: ブラウザのDevToolsで <code>/api/v1/runs/open</code> が「pending」になっていないか確認してください。
                </li>
              </ul>
              <div style={{ marginTop: 10 }}>
                <p className="eyebrow subtle">debug</p>
                <code>ticket: {debug.ticketMasked}</code>
                <code>deviceKey: {debug.deviceKey}</code>
                <code>apiBase: {debug.apiBase || '(empty)'}</code>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const step = snapshot.step;
  const isSceneVideoStep = step?.type === 'scene' && step.bg.kind === 'video' && Boolean(step.bg.src);
  const canAdvance = !snapshot.isCompleted && !waiting && (!isSceneVideoStep || sceneVideoEnded);

  // スタート画面（本番・dev 共通）。dev も本番と同じ全画面フレームで描画し、毎回のページ
  // スクロールを無くす。これにより「モックでは品質判断ができない」を解消する。
  if (shouldShowStartScreen && startScreen) {
    return (
      <div className="player-shell player-shell--production">
        <StartScreen
          config={startScreen}
          onStart={handleStartGame}
          hasSavedProgress={hasSavedProgress}
          onResume={handleResumeGame}
          savedAt={savedProgressSummary?.savedAt}
          scenarioId={scenarioId ?? undefined}
        />
        {/* P2 Wave B: 起動「……接続」演出。開始ボタン押下後、phase が 'playing' に切替わるまでの
            duration の間だけスタート画面の上に接続シーケンスを見せる（fixed/pointer-events:none）。 */}
        <SupernaturalOverlay />
        {/* dev のみ: スタート画面をスキップする QA ショートカット（本番フレームを汚さない fixed トグル） */}
        {!productionMode && (
          <button className="dev-qa__skip-start" onClick={handleStartGame}>
            スタート画面をスキップ
          </button>
        )}
      </div>
    );
  }

  // P9-1: 図鑑記録の冪等キー。live はサーバ採番の runId（ラン毎に一意・リロード跨ぎで安定）を、
  // mock/preview は App 管理の playthroughId を使う（mock の context.runId は固定文字列のため不可）。
  const collectionRunId = mode === 'live' && context?.runId ? context.runId : playthroughId;

  // 本番のゲームフレーム（1画面フィット・本番の進行アフォーダンス一式）。
  // dev でもこの同一フレームを再利用するため共通化する。本番の見た目・挙動は不変。
  const gameFrame = (
    <div className="player-shell player-shell--production">
      <div className="player-viewport--fullscreen">
        {/* R2 感覚層: step/state に応じた BGM をグローバル音エンジンへ委譲（不可視・音のみ） */}
        <BgmController step={step} state={snapshot.state} />
        {/* グローバル音量/ミュート UI（常設・既定は音あり。内部用語を出さない） */}
        {playerPhase === 'playing' && !snapshot.isCompleted && <GlobalAudioControl />}
        {/* P2 Wave B: 全画面「気配（超常）」オーバーレイ。StepLayoutRenderer と peer・
            z-index は基盤側で 30 固定（HUD より下・bg/コンテンツより上）・pointer-events:none で操作を妨げない。
            mood 連動の極薄ノイズ・深夜の濃さ・起動直後の接続収束をここで描く。 */}
        <SupernaturalOverlay />
        <StepOverlayLayer overlays={(step as any)?.overlays} layerFilter="back" />
        <StepLayoutRenderer
          step={step}
          waiting={waiting}
          state={snapshot.state}
          canAdvance={canAdvance}
          onRequestAdvance={() => {
            void actions.advance();
          }}
          autoAdvanceProgress={autoAdvanceArmKey ? { armKey: autoAdvanceArmKey, delayMs: autoAdvanceDelayMs } : null}
          onDialogRevealDoneChange={setDialogRevealDone}
          onSceneVideoEnded={(ended) => setSceneVideoEnded(ended)}
          onEndingAction={handleEndingAction}
          onGenerateCertificate={mode === 'live' || mode === 'mock' ? actions.generateCertificate : undefined}
          onCompleteEnding={actions.completeEnding}
          playthroughId={collectionRunId}
          scenarioId={scenarioId ?? undefined}
          onLineDialogChoiceSelect={(choiceIndex) => { void actions.selectLineDialogChoice(choiceIndex); }}
          onLineDialogFreeInputSubmit={(text) => { void actions.submitLineDialogFreeInput(text); }}
          onLineDialogComplete={() => { void actions.completeLineDialog(); }}
        />
        <StepOverlayLayer overlays={(step as any)?.overlays} layerFilter="front" />
        {waiting?.type === 'minigame' && (
          <div className="minigame-overlay">
            <MinigameHost waiting={waiting} runtimeContext={context} onSubmit={actions.submitMinigameResult} productionMode currentBackground={currentBackground} />
          </div>
        )}
        {waiting && waiting.type !== 'minigame' && (
          <StepInteractionOverlay
            waiting={waiting}
            isBusy={isBusy}
            error={error}
            onCompleteReward={actions.completeReward}
            onSubmitFreeInput={actions.submitFreeInput}
            onSkipFreeInput={actions.skipFreeInput}
            onSelectDialogChoice={actions.selectDialogChoice}
            onSubmitSpotGateAnswer={actions.submitSpotGateAnswer}
          />
        )}
        {snapshot.isCompleted && <CompletionOverlay ticketStatus={context?.ticketStatus} />}
        {/* ステータスHUD */}
        {scenarioUi?.statusDisplay && playerPhase === 'playing' && (
          <StatusHUD
            config={scenarioUi.statusDisplay}
            variables={snapshot.state.variables}
          />
        )}
        {/* 手がかりウィンドウボタン */}
        {routeMap && playerPhase === 'playing' && !snapshot.isCompleted && (
          <RouteMapButton
            isOpen={routeMapOpen}
            onClick={() => setRouteMapOpen((value) => !value)}
            position={routeMap.buttonPosition}
          />
        )}
        {hasClues && playerPhase === 'playing' && (
          <button
            className="clue-window-btn"
            onClick={() => setClueWindowOpen(true)}
            aria-label="手がかりを確認"
          >
            <BookOpen className="clue-window-btn__icon" aria-hidden="true" />
            {unreadClueCount > 0 && (
              <span className="clue-badge">{unreadClueCount}</span>
            )}
          </button>
        )}
        {/* LINE履歴パネルボタン（会話記録がある場合に自動表示、明示的にfalseで無効化可能） */}
        {scenarioUi?.lineHistoryPanel?.enabled !== false && playerPhase === 'playing' && step?.type !== 'line_dialog' && (snapshot.state.lineHistory?.conversations?.length ?? 0) > 0 && (
          <LineHistoryButton
            onClick={() => setLineHistoryOpen(true)}
            position={scenarioUi?.lineHistoryPanel?.defaultButtonPosition ?? 'bottom-right'}
          />
        )}
      </div>
      {/* 手がかりウィンドウ */}
      {hasClues && (
        <ClueWindow
          clueDefinitions={clueDefinitions}
          clueState={clueState}
          onStateChange={setClueState}
          onClose={() => setClueWindowOpen(false)}
          isOpen={clueWindowOpen}
        />
      )}
      <RouteMapWindow
        routeMap={routeMap}
        isOpen={routeMapOpen}
        onClose={() => setRouteMapOpen(false)}
        onDiscoverHotspot={(spotId, hotspotId, hotspotActions) =>
          actions.discoverRouteMapHotspot(spotId, hotspotId, hotspotActions)
        }
      />
      {/* LINE履歴パネル */}
      <LineHistoryPanel
        isOpen={lineHistoryOpen}
        onClose={() => setLineHistoryOpen(false)}
        lineHistory={snapshot.state.lineHistory}
      />
    </div>
  );

  // 本番モード: ゲームフレームのみ（デバッグUI非表示）。
  if (productionMode) {
    return gameFrame;
  }

  // 開発(モック)モード: 本番と同一フレームで描画し、デバッグ群は固定 QA ドロワーへ集約する。
  // ページスクロールは出さない（フレームが 100dvh フィット）。機能は削除せず移設のみ。
  return (
    <>
      {gameFrame}
      <DevQaPanel
        mode={mode}
        context={{ ...context, scenarioTitle: context?.scenarioTitle }}
        node={snapshot.node}
        currentStepIndex={snapshot.state.currentStepIndex}
        variableDefinitions={variableDefinitions}
        variables={snapshot.state.variables}
        runState={snapshot.state}
        canAdvance={canAdvance}
        isBusy={isBusy}
        isCompleted={snapshot.isCompleted}
        onForceAdvance={() => { void actions.advance(); }}
        lastSavedAt={lastSavedAt}
        onShowErrorUi05={() => setUi05Open(true)}
      />
      {ui05Open && (
        <div className="completion-overlay" role="dialog" aria-modal="true">
          <ErrorUi05
            title="エラーUI（UI05）表示テスト"
            message="これはPhase Aの地雷チェック用のテスト表示です。導線（再試行/ホーム/問い合わせ/デバッグ情報）を確認してください。"
            supportUrl={ui05SupportUrl}
            debugLines={[
              `mode: ${mode}`,
              `runId: ${context?.runId ?? '(none)'}`,
              `deviceKey: ${context?.deviceKey ?? '(none)'}`,
              `scenario: ${context?.scenarioTitle ?? '(none)'}`
            ]}
            onRetry={() => setUi05Open(false)}
            onHome={() => {
              const params = new URLSearchParams(window.location.search);
              params.delete('ticket');
              params.set('mode', 'mock');
              window.location.search = params.toString() ? `?${params.toString()}` : '';
            }}
            onClose={() => setUi05Open(false)}
          />
        </div>
      )}
    </>
  );
}

/** P8-3「シェルの声」: ticket の内部 enum（STARTED/COMPLETED）を世界内語彙へ写像する。
 *  写像できない値は表示しない（内部値を画面に漏らさない・guide §4.6）。 */
function ticketStatusLabel(status?: string): string | null {
  if (!status) return null;
  const labels: Record<string, string> = {
    STARTED: '進行中',
    COMPLETED: '解決済み'
  };
  return labels[status.toUpperCase()] ?? null;
}

function CompletionOverlay({ ticketStatus }: { ticketStatus?: string }) {
  const statusLabel = ticketStatusLabel(ticketStatus);
  return (
    <div className="completion-overlay">
      <div className="completion-card">
        {/* P8-3: 英語 eyebrow（Run Completed）を端末の状態断片（S1）へ。 */}
        <p className="eyebrow">……記録済み</p>
        <h2>お疲れさまでした！</h2>
        <p>結果は自動保存済みです。必要に応じてサポートへ共有してください。</p>
        {statusLabel && <p className="eyebrow subtle">受付票: {statusLabel}</p>}
      </div>
    </div>
  );
}

function ErrorUi05({
  title,
  message,
  supportUrl,
  debugLines,
  onRetry,
  onHome,
  onClose,
  // P8-0: 本番では「UI05 · Error」の内部呼称を出さない（null で非表示）。dev 既定は従来どおり。
  eyebrow = 'UI05 · Error'
}: {
  title: string;
  message: string;
  supportUrl?: string;
  debugLines?: string[];
  onRetry: () => void;
  onHome?: () => void;
  onClose?: () => void;
  eyebrow?: string | null;
}) {
  return (
    <div className="completion-card" role="alert">
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <h2>{title}</h2>
      <p style={{ marginTop: 8 }}>{message}</p>
      <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="primary-btn" type="button" onClick={onRetry}>
          再試行
        </button>
        {onHome && (
          <button className="secondary-btn" type="button" onClick={onHome}>
            ホームへ
          </button>
        )}
        {supportUrl && (
          <a className="ghost-btn" href={supportUrl} target="_blank" rel="noreferrer">
            お問い合わせ
          </a>
        )}
        {onClose && (
          <button className="ghost-btn" type="button" onClick={onClose}>
            閉じる
          </button>
        )}
      </div>
      {Array.isArray(debugLines) && debugLines.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <p className="eyebrow subtle">debug</p>
          {debugLines.map((line) => (
            <code key={line}>{line}</code>
          ))}
        </div>
      )}
    </div>
  );
}

/** ステップに紐づくオーバーレイ要素（画像・テキスト）を描画するレイヤー */
function StepOverlayLayer({ overlays, layerFilter }: { overlays?: OverlayElement[]; layerFilter?: 'front' | 'back' }) {
  if (!overlays || overlays.length === 0) return null;
  const filtered = layerFilter
    ? overlays.filter((ov) => (ov.layer ?? 'front') === layerFilter)
    : overlays;
  if (filtered.length === 0) return null;
  const zClass = layerFilter === 'back' ? 'step-overlay-layer--back' : 'step-overlay-layer';
  return (
    <div className={zClass}>
      {filtered.map((ov) => {
        // 画像オーバーレイは widthPct/heightPct を明示サイズとして適用し object-fit: contain を効かせる。
        // テキストは内容で高さが伸びるので min-height を使う。
        // どちらも viewport を超えないよう max-width/max-height: 100% でクランプする。
        const isImage = ov.type === 'image';
        const sizingStyle = isImage
          ? { width: `${ov.widthPct}%`, height: `${ov.heightPct}%` }
          : { width: `${ov.widthPct}%`, minHeight: `${ov.heightPct}%` };
        return (
          <div
            key={ov.id}
            className="step-overlay-element"
            style={{
              left: `${ov.xPct}%`,
              top: `${ov.yPct}%`,
              ...sizingStyle,
              maxWidth: '100%',
              maxHeight: '100%',
              transform: 'translate(-50%, -50%)'
            }}
          >
            {ov.type === 'image' && ov.assetId && (
              <img
                src={resolveMediaRefToUrl(ov.assetId) ?? ov.assetId}
                alt=""
                className="step-overlay-image"
                draggable={false}
              />
            )}
            {ov.type === 'text' && ov.content && (
              <div
                className="step-overlay-text"
                style={{
                  fontSize: ov.style?.fontSize || 16,
                  fontWeight: ov.style?.fontWeight || 'normal',
                  color: ov.style?.color || '#ffffff'
                }}
              >
                {ov.content}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

type StartupDebugInfo = {
  ticketMasked: string;
  deviceKey: string;
  apiBase: string;
};

function getStartupDebugInfo(): StartupDebugInfo | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const ticket = params.get('ticket') ?? '';
    const storedDeviceKey = window.localStorage.getItem('player_device_key') ?? '(missing)';
    const apiBase = (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_API_URL ?? '';

    return {
      ticketMasked: maskTicket(ticket),
      deviceKey: storedDeviceKey,
      apiBase
    };
  } catch {
    return null;
  }
}

function maskTicket(ticket: string): string {
  if (!ticket) {
    return '(none)';
  }
  if (ticket.length <= 8) {
    return `${ticket.slice(0, 2)}***`;
  }
  return `${ticket.slice(0, 4)}***${ticket.slice(-4)}`;
}

